import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  RefreshCw,
  ExternalLink,
  MessageCircle,
  CheckCircle,
  AlertTriangle,
  Loader2
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Format financial values - no decimals for cleaner display
const formatFinancialValue = (value) => {
  if (!value || value === 'N/A' || value === '-') return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  if (num >= 1e12) return `$${Math.round(num / 1e12)}T`;
  if (num >= 1e9) return `$${Math.round(num / 1e9)}B`;
  if (num >= 1e6) return `$${Math.round(num / 1e6)}M`;
  return `$${Math.round(num).toLocaleString()}`;
};

const formatRatio = (value) => {
  if (!value || value === 'N/A' || value === '-') return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toFixed(2);
};

function PollSpreadsheet({ selectedPoll, sortConfig: externalSortConfig, isDarkMode = false, onSort, onTickerClick, onPollUpdated, onDocuments, onSchedule, onShare, onWatchlist, onDelete, onAddTickers }) {
  const [tickerData, setTickerData] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const sortConfig = externalSortConfig || { key: 'ticker', direction: 'asc' };
  const [expandedRowTicker, setExpandedRowTicker] = useState(null);
  const [validationStatus, setValidationStatus] = useState({}); // { replyId: { status: 'checking'|'valid'|'deleted', cached: bool } }
  const [showAddTickerInput, setShowAddTickerInput] = useState(false);
  const [newTickerInput, setNewTickerInput] = useState('');
  const [addingTicker, setAddingTicker] = useState(false);

  // Add ticker to the current poll/list
  const handleAddTicker = async () => {
    if (!newTickerInput.trim() || !selectedPoll?.id) return;
    
    const ticker = newTickerInput.trim().toUpperCase().replace('$', '');
    if (!ticker || ticker.length > 5) return;
    
    setAddingTicker(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/fishing-threads/${selectedPoll.id}/add-ticker`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticker })
      });
      
      if (response.ok) {
        setNewTickerInput('');
        // Trigger refresh of the poll data
        if (onAddTickers) {
          onAddTickers(selectedPoll.id);
        }
      }
    } catch (error) {
      console.error('Error adding ticker:', error);
    } finally {
      setAddingTicker(false);
    }
  };

  // Validate tweet exists on X (background check)
  const validateReply = useCallback(async (reply) => {
    if (!reply?.id || !reply?.url) return;
    
    const replyId = reply.id;
    
    // Skip if already validated recently
    if (validationStatus[replyId]?.status === 'valid' || validationStatus[replyId]?.status === 'deleted') {
      return;
    }
    
    setValidationStatus(prev => ({ ...prev, [replyId]: { status: 'checking', cached: false } }));
    
    try {
      // Extract tweet ID from URL
      const tweetIdMatch = reply.url.match(/status\/(\d+)/);
      const tweetId = tweetIdMatch ? tweetIdMatch[1] : reply.id;
      
      const response = await fetch(`${API_BASE_URL}/api/tweets/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tweet_id: tweetId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setValidationStatus(prev => ({
          ...prev,
          [replyId]: {
            status: data.is_deleted ? 'deleted' : 'valid',
            cached: data.from_cache || false
          }
        }));
      } else {
        // API error - assume valid to not block user
        setValidationStatus(prev => ({ ...prev, [replyId]: { status: 'valid', cached: false } }));
      }
    } catch (error) {
      console.error('Error validating reply:', error);
      setValidationStatus(prev => ({ ...prev, [replyId]: { status: 'valid', cached: false } }));
    }
  }, [validationStatus]);

  const handleRowClick = (ticker, replies) => {
    const newExpanded = expandedRowTicker === ticker ? null : ticker;
    setExpandedRowTicker(newExpanded);
    
    // Validate replies in background when expanding
    if (newExpanded && replies && replies.length > 0) {
      replies.forEach(reply => validateReply(reply));
    }
  };

  // Fetch ticker metrics for selected poll
  const fetchTickerMetrics = useCallback(async (tickers, tickerToAuthorMap, tickerToRepliesMap = {}) => {
    if (!tickers || tickers.length === 0) {
      setTickerData([]);
      return;
    }

    setLoadingMetrics(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ticker-metrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers })
      });

      if (response.ok) {
        const data = await response.json();
        // Handle both dictionary and array formats from API
        let metricsArray = [];
        if (Array.isArray(data.metrics)) {
          metricsArray = data.metrics;
        } else if (data.metrics && typeof data.metrics === 'object') {
          // Convert dictionary format to array
          metricsArray = Object.entries(data.metrics).map(([ticker, metrics]) => ({
            ticker,
            ...metrics
          }));
        }
        
        // Ensure all requested tickers are in the result, even if no metrics found
        const enrichedData = tickers.map(ticker => {
          const metric = metricsArray.find(m => m.ticker === ticker) || {};
          return {
            ticker,
            company_name: metric.company_name || '-',
            market_cap: metric.market_cap || '-',
            enterprise_value: metric.enterprise_value || '-',
            ev_to_sales: metric.ev_to_sales || '-',
            forward_pe: metric.forward_pe || '-',
            suggested_by: tickerToAuthorMap[ticker] || 'Unknown',
            replies: tickerToRepliesMap[ticker] || []
          };
        });
        setTickerData(enrichedData);
      } else {
        const fallbackData = tickers.map(ticker => ({
          ticker,
          company_name: '-',
          suggested_by: tickerToAuthorMap[ticker] || 'Unknown',
          replies: tickerToRepliesMap[ticker] || [],
          market_cap: '-',
          enterprise_value: '-',
          ev_to_sales: '-',
          forward_pe: '-'
        }));
        setTickerData(fallbackData);
      }
    } catch (err) {
      console.error('Error fetching ticker metrics:', err);
      const fallbackData = tickers.map(ticker => ({
        ticker,
        company_name: '-',
        suggested_by: tickerToAuthorMap[ticker] || 'Unknown',
        replies: tickerToRepliesMap[ticker] || [],
        market_cap: '-',
        enterprise_value: '-',
        ev_to_sales: '-',
        forward_pe: '-'
      }));
      setTickerData(fallbackData);
    } finally {
      setLoadingMetrics(false);
    }
  }, []);

  // Load metrics when poll changes
  useEffect(() => {
    if (selectedPoll) {
      const tickerToAuthorMap = {};
      const tickerToRepliesMap = {}; // Now stores ARRAY of replies per ticker
      const uniqueTickers = new Set();
      
      // Collect all replies per ticker
      (selectedPoll.replies || []).forEach(reply => {
        (reply.tickers || []).forEach(ticker => {
          uniqueTickers.add(ticker);
          
          // Initialize array if needed
          if (!tickerToRepliesMap[ticker]) {
            tickerToRepliesMap[ticker] = [];
          }
          // Add this reply to the ticker's replies array
          tickerToRepliesMap[ticker].push(reply);
          
          // Set first author as the "suggested_by"
          if (!tickerToAuthorMap[ticker]) {
            tickerToAuthorMap[ticker] = `@${reply.author_username}`;
          }
        });
      });
      
      // Also handle tickers_discovered that might not have reply data
      // Use ticker_details as fallback for reply info
      const tickerDetails = selectedPoll.ticker_details || {};
      
      (selectedPoll.tickers_discovered || []).forEach(ticker => {
        uniqueTickers.add(ticker);
        if (!tickerToAuthorMap[ticker]) {
          // Try to get from ticker_details
          const details = tickerDetails[ticker];
          if (details) {
            tickerToAuthorMap[ticker] = details.source_author || '-';
            // Create a synthetic reply object from ticker_details if no replies exist
            if (!tickerToRepliesMap[ticker] || tickerToRepliesMap[ticker].length === 0) {
              tickerToRepliesMap[ticker] = [{
                text: details.source_tweet,
                author_username: (details.source_author || '@unknown').replace('@', ''),
                author_name: (details.source_author || '@unknown').replace('@', ''),
                url: details.source_url,
                created_at: details.discovered_at,
                tickers: [ticker]
              }];
            }
          } else {
            tickerToAuthorMap[ticker] = '-';
          }
        }
      });
      
      // Use unique tickers only (deduplicated)
      fetchTickerMetrics(Array.from(uniqueTickers), tickerToAuthorMap, tickerToRepliesMap);
    } else {
      setTickerData([]);
    }
  }, [selectedPoll, selectedPoll?.tickers_discovered?.length, selectedPoll?.ticker_details, fetchTickerMetrics]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!tickerData.length) return [];
    
    // Parse values with magnitude suffixes (T, B, M, K)
    const parseWithMagnitude = (val) => {
      if (typeof val === 'number') return val;
      if (val == null || val === '' || val === 'N/A' || val === 'Pro plan required') return null;
      const str = String(val).replace(/[$,%]/g, '').trim();
      const match = str.match(/^(-?\d+\.?\d*)\s*([TBMKtbmk])?$/);
      if (match) {
        let num = parseFloat(match[1]);
        const suffix = (match[2] || '').toUpperCase();
        if (suffix === 'T') num *= 1e12;
        else if (suffix === 'B') num *= 1e9;
        else if (suffix === 'M') num *= 1e6;
        else if (suffix === 'K') num *= 1e3;
        return num;
      }
      return parseFloat(str);
    };
    
    return [...tickerData].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      
      // Parse with magnitude
      const numA = parseWithMagnitude(valA);
      const numB = parseWithMagnitude(valB);
      
      // Push nulls to the end
      if (numA === null && numB === null) return 0;
      if (numA === null) return 1;
      if (numB === null) return -1;
      
      if (!isNaN(numA) && !isNaN(numB)) {
        return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
      }
      
      const strA = String(valA || '').toLowerCase();
      const strB = String(valB || '').toLowerCase();
      
      if (sortConfig.direction === 'asc') {
        return strA.localeCompare(strB);
      }
      return strB.localeCompare(strA);
    });
  }, [tickerData, sortConfig]);

  const handleSort = (key) => {
    if (onSort) {
      onSort(key);
    }
  };

  // No poll selected - show educational placeholder
  if (!selectedPoll) {
    return (
      <div className="px-2 sm:px-4 py-4">
        {/* Empty State Header */}
        <div className="text-center py-8 mb-8">
          <MessageCircle className={`w-16 h-16 mx-auto mb-4 opacity-50 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
          <p className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>No poll selected</p>
          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Select a poll from the dropdown above or create a new one!</p>
        </div>

        {/* Educational Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Two Ways to Use */}
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
            <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>Two Ways to Use Investment Polls</h4>
            
            <div className="space-y-4">
              <div className={`p-3 rounded-lg border-l-4 border-blue-500 ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                <h5 className={`font-medium mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-800'}`}>Save Others&apos; Polls</h5>
                <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  Reply to any tweet asking for stock ideas and mention <span className={`font-mono px-1 rounded ${isDarkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>@savetotickler</span>
                </p>
              </div>
              
              <div className={`p-3 rounded-lg border-l-4 border-orange-500 ${isDarkMode ? 'bg-orange-900/30' : 'bg-orange-50'}`}>
                <h5 className={`font-medium mb-2 ${isDarkMode ? 'text-orange-400' : 'text-orange-800'}`}>Create Your Own Polls</h5>
                <ol className={`text-sm ${isDarkMode ? 'text-orange-300' : 'text-orange-700'} list-decimal list-inside space-y-1`}>
                  <li>Post a question on X with <span className={`font-mono px-1 rounded ${isDarkMode ? 'bg-orange-900/50' : 'bg-orange-100'}`}>@savetotickler</span></li>
                  <li>Bookmark your own tweet</li>
                  <li>Sync your bookmarks in Tickler (Tickers tab → Sync)</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Example Questions */}
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
            <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>Example Questions to Ask</h4>
            
            <div className="space-y-3">
              <div>
                <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Industry Focus</div>
                <div className={`p-2 text-xs italic rounded border-l-4 border-green-500 ${isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-50 text-slate-600'}`}>
                  &quot;What&apos;s your best AI stock pick for 2025? @savetotickler&quot;
                </div>
              </div>
              <div>
                <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Market Cap</div>
                <div className={`p-2 text-xs italic rounded border-l-4 border-green-500 ${isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-50 text-slate-600'}`}>
                  &quot;Drop your favorite small-cap growth stocks @savetotickler&quot;
                </div>
              </div>
              <div>
                <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Theme Based</div>
                <div className={`p-2 text-xs italic rounded border-l-4 border-green-500 ${isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-50 text-slate-600'}`}>
                  &quot;Best renewable energy plays for the next decade? @savetotickler&quot;
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 pb-2 pt-1">
      {/* Poll Info Card - Compact */}
      <div className={`mb-2 p-2 rounded-lg border ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-gradient-to-r from-orange-50 to-slate-50 border-orange-200'}`}>
        <div className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
          {selectedPoll.title || 'Investment Poll'}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs mt-1.5">
          <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
            @{selectedPoll.original_tweet?.author_username}
          </span>
          <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>·</span>
          <span className={`flex items-center gap-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <svg className="w-3 h-3 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <rect x="4" y="3" width="16" height="12" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5 16h14" strokeLinecap="round"/>
              <path d="M6 18h12" strokeLinecap="round"/>
            </svg>
            {selectedPoll.ticker_count || 0} ideas
          </span>
          <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>·</span>
          <span className={`flex items-center gap-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
            {selectedPoll.reply_count || 0} replies
          </span>
          <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>·</span>
          <a 
            href={selectedPoll.original_tweet?.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-1 hover:underline ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
          >
            <ExternalLink className="w-3 h-3" />
            View on X
          </a>
        </div>
        
        {/* Add Ticker Button - Shows for custom lists and user's own polls */}
        {(selectedPoll.source === 'custom_list' || selectedPoll.is_own_poll) && (
          <div className="mt-2">
            {showAddTickerInput ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newTickerInput}
                  onChange={(e) => setNewTickerInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTicker();
                    } else if (e.key === 'Escape') {
                      setShowAddTickerInput(false);
                      setNewTickerInput('');
                    }
                  }}
                  placeholder="Enter ticker (e.g., AAPL)"
                  className={`flex-1 px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-600 text-white placeholder-slate-400' 
                      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
                  }`}
                  autoFocus
                  maxLength={5}
                />
                <button
                  onClick={handleAddTicker}
                  disabled={!newTickerInput.trim() || addingTicker}
                  className="px-3 py-1.5 text-sm bg-orange-500 hover:bg-orange-600 disabled:bg-slate-400 text-white rounded-lg font-medium transition-colors flex items-center gap-1"
                >
                  {addingTicker ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                    </svg>
                  )}
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowAddTickerInput(false);
                    setNewTickerInput('');
                  }}
                  className={`px-2 py-1.5 text-sm rounded-lg transition-colors ${
                    isDarkMode ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAddTickerInput(true)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800'
                }`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                </svg>
                Add Ticker
              </button>
            )}
          </div>
        )}
      </div>

      {/* Poll Results Section - Shows when poll has options with votes */}
      {selectedPoll.poll_options && selectedPoll.poll_options.length > 0 && (
        <div className={`mb-3 p-4 rounded-lg border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 00-2-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
              Poll Results
            </h3>
            <div className="flex items-center gap-2">
              {selectedPoll.poll_status === 'closed' && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'}`}>
                  Closed
                </span>
              )}
              <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {selectedPoll.total_votes?.toLocaleString() || 0} votes
              </span>
            </div>
          </div>
          
          <div className="space-y-2">
            {selectedPoll.poll_options
              .sort((a, b) => (b.votes || 0) - (a.votes || 0))
              .map((option, idx) => {
                const percentage = selectedPoll.total_votes > 0 
                  ? Math.round((option.votes / selectedPoll.total_votes) * 100) 
                  : 0;
                const isWinner = idx === 0 && selectedPoll.poll_status === 'closed';
                
                return (
                  <div key={option.label} className="relative">
                    {/* Background bar */}
                    <div 
                      className={`absolute inset-0 rounded-lg transition-all ${
                        isWinner 
                          ? (isDarkMode ? 'bg-green-900/40' : 'bg-green-100') 
                          : (isDarkMode ? 'bg-slate-700/50' : 'bg-slate-100')
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                    
                    {/* Content */}
                    <div className={`relative flex items-center justify-between p-2.5 rounded-lg border ${
                      isWinner 
                        ? (isDarkMode ? 'border-green-700' : 'border-green-300') 
                        : (isDarkMode ? 'border-slate-600' : 'border-slate-200')
                    }`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                          isWinner 
                            ? (isDarkMode ? 'bg-green-700 text-green-100' : 'bg-green-500 text-white') 
                            : (isDarkMode ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600')
                        }`}>
                          {option.label.toUpperCase()}
                        </span>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          {option.ticker && <span className="text-orange-500 font-semibold">${option.ticker}</span>}
                          {option.ticker && ' - '}
                          {option.text?.replace(/^\$\w+\s*-?\s*/, '') || option.text}
                        </span>
                        {isWinner && (
                          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {option.votes?.toLocaleString() || 0}
                        </span>
                        <span className={`text-sm font-bold min-w-[3rem] text-right ${
                          isWinner 
                            ? (isDarkMode ? 'text-green-400' : 'text-green-600') 
                            : (isDarkMode ? 'text-slate-300' : 'text-slate-700')
                        }`}>
                          {percentage}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Table Container */}
      <div className={`rounded-lg overflow-hidden border shadow-sm ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
        {loadingMetrics ? (
          <div className="p-4 text-center">
            <RefreshCw className={`w-6 h-6 mx-auto mb-2 animate-spin ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Loading ticker data...</p>
          </div>
        ) : sortedData.length === 0 ? (
          <div className={`p-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            <p>No tickers discovered yet. Click &quot;Refresh&quot; to fetch new replies.</p>
          </div>
        ) : (
          <div className="overflow-x-auto relative">
            <table className="w-full text-sm">
              <thead className={`text-xs uppercase tracking-wide ${isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
                <tr>
                  <th 
                    className={`px-3 py-2 text-left sticky left-0 cursor-pointer hover:text-orange-500 ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}
                    onClick={() => onSort && onSort('ticker')}
                  >
                    <div className="flex items-center">
                      Ticker
                      {sortConfig.key === 'ticker' 
                        ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                        : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                      }
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                    onClick={() => onSort && onSort('market_cap')}
                  >
                    <div className="flex items-center justify-center">
                      MCap
                      {sortConfig.key === 'market_cap' 
                        ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                        : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                      }
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                    onClick={() => onSort && onSort('enterprise_value')}
                  >
                    <div className="flex items-center justify-center">
                      EV
                      {sortConfig.key === 'enterprise_value' 
                        ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                        : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                      }
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                    onClick={() => onSort && onSort('forward_pe')}
                  >
                    <div className="flex items-center justify-center">
                      P/E
                      {sortConfig.key === 'forward_pe' 
                        ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                        : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                      }
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-center cursor-pointer hover:text-orange-500"
                    onClick={() => onSort && onSort('ev_to_sales')}
                  >
                    <div className="flex items-center justify-center">
                      EV/S
                      {sortConfig.key === 'ev_to_sales' 
                        ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                        : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                      }
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                    onClick={() => onSort && onSort('price_to_book')}
                  >
                    <div className="flex items-center justify-center">
                      P/B
                      {sortConfig.key === 'price_to_book' 
                        ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                        : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                      }
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                    onClick={() => onSort && onSort('gross_margin')}
                  >
                    <div className="flex items-center justify-center">
                      Gross
                      {sortConfig.key === 'gross_margin' 
                        ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                        : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                      }
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                    onClick={() => onSort && onSort('operating_margin')}
                  >
                    <div className="flex items-center justify-center">
                      Op Mgn
                      {sortConfig.key === 'operating_margin' 
                        ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                        : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                      }
                    </div>
                  </th>
                  <th 
                    className="px-3 py-2 text-center cursor-pointer hover:text-orange-500 hidden sm:table-cell"
                    onClick={() => onSort && onSort('dividend_yield')}
                  >
                    <div className="flex items-center justify-center">
                      Div
                      {sortConfig.key === 'dividend_yield' 
                        ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-orange-500" /> : <ArrowDown className="w-3 h-3 ml-1 text-orange-500" />)
                        : <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />
                      }
                    </div>
                  </th>
                  <th className="px-2 py-2 text-right hidden sm:table-cell"></th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-slate-600' : 'divide-slate-100'}`}>
                {sortedData.map((row, idx) => (
                  <React.Fragment key={row.ticker + idx}>
                    <tr 
                      className={`cursor-pointer ${isDarkMode ? 'bg-slate-900 hover:bg-slate-800' : 'bg-white hover:bg-slate-50'} ${expandedRowTicker === row.ticker ? (isDarkMode ? 'bg-slate-800' : 'bg-slate-100') : ''}`}
                      onClick={() => handleRowClick(row.ticker, row.replies)}
                    >
                      <td className={`px-3 py-2 sticky left-0 ${isDarkMode ? 'bg-slate-900' : 'bg-white'} ${expandedRowTicker === row.ticker ? (isDarkMode ? 'bg-slate-800' : 'bg-slate-100') : ''}`}>
                        <span className={`font-mono font-bold text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                          ${row.ticker}
                        </span>
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formatFinancialValue(row.market_cap)}
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formatFinancialValue(row.enterprise_value)}
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formatRatio(row.forward_pe)}
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formatRatio(row.ev_to_sales)}
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {formatRatio(row.price_to_book)}
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {row.gross_margin ? `${formatRatio(row.gross_margin * 100)}%` : '-'}
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {row.operating_margin ? `${formatRatio(row.operating_margin * 100)}%` : '-'}
                      </td>
                      <td className={`px-3 py-2 text-center font-mono text-sm hidden sm:table-cell ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {row.dividend_yield ? `${formatRatio(row.dividend_yield * 100)}%` : '-'}
                      </td>
                      {/* Action buttons - hidden on mobile, shown on desktop - Order: File, Folder, Share, Delete */}
                      <td className="px-2 py-2 hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          {/* File button */}
                          <button
                            onClick={() => onDocuments?.(row)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-all ${isDarkMode ? 'border-slate-400 text-slate-300 hover:bg-slate-500 hover:text-white hover:border-slate-500' : 'border-slate-400 text-slate-600 hover:bg-slate-500 hover:text-white'}`}
                            title={`Dossier for $${row.ticker}`}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                            </svg>
                            <span>File</span>
                          </button>
                          {/* Folder button */}
                          <button
                            onClick={() => onSchedule?.(row)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-all ${isDarkMode ? 'border-purple-400 text-purple-400 hover:bg-purple-500 hover:text-white hover:border-purple-500' : 'border-purple-400 text-purple-600 hover:bg-purple-500 hover:text-white'}`}
                            title={`Add $${row.ticker} to folder`}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                            </svg>
                            <span>Folder</span>
                          </button>
                          {/* Share button */}
                          <button
                            onClick={() => onShare?.(row)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-all ${isDarkMode ? 'border-orange-400 text-orange-400 hover:bg-orange-500 hover:text-white hover:border-orange-500' : 'border-orange-400 text-orange-600 hover:bg-orange-500 hover:text-white'}`}
                            title={`Share $${row.ticker}`}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
                            </svg>
                            <span>Share</span>
                          </button>
                          {/* Delete button */}
                          <button
                            onClick={() => onDelete?.(row)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-all ${isDarkMode ? 'border-red-400 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500' : 'border-red-400 text-red-500 hover:bg-red-500 hover:text-white'}`}
                            title={`Remove $${row.ticker} from poll`}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                            <span>Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded Row - Shows tweet content + action buttons on both mobile and desktop */}
                    {expandedRowTicker === row.ticker && (
                    <tr>
                      <td colSpan={10} className="p-0">
                        <div className={`px-3 py-2 border-t ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                          {/* Tweet content with validation status */}
                          {row.replies && row.replies.length > 0 && (
                            <div className="space-y-2 mb-2 max-h-40 overflow-y-auto">
                              {row.replies.map((reply, replyIdx) => {
                                const status = validationStatus[reply.id];
                                const isDeleted = status?.status === 'deleted';
                                
                                return (
                                  <div key={replyIdx} className={`flex items-start gap-2 ${isDeleted ? 'opacity-50' : ''}`}>
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                      <span className={`text-xs font-bold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                        {(reply.author_username || 'U')[0].toUpperCase()}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <span className={`text-xs font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                          @{reply.author_username}
                                        </span>
                                        {/* Validation status indicator */}
                                        {status?.status === 'checking' && (
                                          <Loader2 className={`w-3 h-3 animate-spin ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                                        )}
                                        {status?.status === 'valid' && (
                                          <CheckCircle className="w-3 h-3 text-green-500" />
                                        )}
                                        {status?.status === 'deleted' && (
                                          <AlertTriangle className="w-3 h-3 text-amber-500" />
                                        )}
                                      </div>
                                      {isDeleted ? (
                                        <p className={`text-xs italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                          This post has been removed from X
                                        </p>
                                      ) : (
                                        <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                          {reply.text || reply.content || 'No content available'}
                                        </p>
                                      )}
                                      {reply.url && !isDeleted && (
                                        <a 
                                          href={reply.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`inline-flex items-center gap-1 text-xs mt-1 ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                          View on X
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Action buttons row - Mobile only (desktop has inline buttons) */}
                          <div className={`flex items-center justify-around pt-2 border-t sm:hidden ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <button onClick={(e) => { e.stopPropagation(); onDocuments?.(row); }} className={`flex flex-col items-center flex-1 py-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                              <span className="text-[10px]">File</span>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); onSchedule?.(row); }} className={`flex flex-col items-center flex-1 py-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
                              <span className="text-[10px]">Folder</span>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); onShare?.(row); }} className={`flex flex-col items-center flex-1 py-1 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/></svg>
                              <span className="text-[10px]">Share</span>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); onDelete?.(row); }} className={`flex flex-col items-center flex-1 py-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                              <span className="text-[10px]">Delete</span>
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PollSpreadsheet;
